import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lightning, ArrowRight } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }} />
        <div className="flex items-center gap-2 relative">
          <div className="h-9 w-9 rounded-sm bg-white flex items-center justify-center">
            <Lightning size={20} weight="fill" className="text-black" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">Follow-up OS</div>
            <div className="font-heading text-xl leading-none tracking-tight">Omni Reach</div>
          </div>
        </div>

        <div className="relative">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-4">
            /// unified follow-up
          </div>
          <h2 className="font-heading text-5xl tracking-tight leading-[1.05] mb-6">
            One <span className="text-[#25D366]">WhatsApp</span>.<br />
            One <span className="text-[#E1306C]">Instagram</span>.<br />
            <span className="text-white/40">Zero missed leads.</span>
          </h2>
          <p className="text-white/60 max-w-md">
            Automate direct messages, cadences and follow-ups across WhatsApp Business Cloud and
            Instagram Graph API — from a single command center.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 relative">
          {[
            ["01", "OAuth Meta"],
            ["02", "Cadences"],
            ["03", "Unified inbox"],
          ].map(([n, l]) => (
            <div key={n} className="border-t border-white/10 pt-3">
              <div className="font-mono text-xs text-white/40">{n}</div>
              <div className="text-sm mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">/// login</div>
          <h1 className="font-heading text-4xl tracking-tight mb-8">Enter your workspace.</h1>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-3 outline-none transition-colors"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-3 outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-white text-black rounded-sm px-4 py-3 font-medium flex items-center justify-center gap-2 hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>

          <div className="mt-8 text-sm text-white/60">
            No account yet?{" "}
            <Link data-testid="login-goto-signup" to="/signup" className="text-white underline underline-offset-4 hover:text-white/80 transition-colors">
              Create your workspace
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
